import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';
import { ConfirmComponent, ConfirmDialogData } from '../../../shared/dialogs/confirm/confirm.component';
import { DialogService } from '../../../shared/components/dialog/dialog.service';
import { CopilotDocument } from '../../../_interfaces/document';
import { Dictation } from '../../../_interfaces/dictation';
import { hasDictationTranscription } from '../../../_helpers/transcription';
import { getDictationRetention } from '../../../_helpers/retention';
import { RegenerateBatchWatcherService } from './regenerate-batch-watcher.service';

@Injectable()
export class RegenerateService {
  private readonly errorKeys = new Set<string>();

  constructor(
    private readonly dialog: DialogService,
    private readonly translate: TranslateService,
    private readonly batchWatcher: RegenerateBatchWatcherService,
  ) {}

  // ─── Eligibility ──────────────────────────────────────────────────────────

  canRegenerate(dictation: Dictation | null, selectedDoc: CopilotDocument | null): boolean {
    if (selectedDoc?.approved) return false;
    return this.hasSourceMaterial(dictation);
  }

  canRegenerateAll(dictation: Dictation | null, processedDocuments: CopilotDocument[]): boolean {
    if (!dictation) return false;
    const hasUnapproved = processedDocuments.some(doc => !doc.approved);
    if (!hasUnapproved) return false;
    return this.hasSourceMaterial(dictation);
  }

  hasSourceMaterial(dictation: Dictation | null): boolean {
    if (!getDictationRetention(dictation).canGenerate) return false;
    return hasDictationTranscription(dictation) || (dictation?.copilot?.notes?.length ?? 0) > 0;
  }

  // ─── Confirmation dialogs ─────────────────────────────────────────────────

  confirmRegenerate(): Promise<boolean> {
    const ref = this.dialog.open<ConfirmComponent, ConfirmDialogData, boolean>(ConfirmComponent, {
      data: {
        title: this.translate.instant('Regenerate document'),
        description: this.translate.instant('Regenerating will create a new version of the document in the current tab'),
        warning: this.translate.instant('All the changes / edits you did to your document will be lost'),
        confirmIcon: 'refresh',
        cancelText: this.translate.instant('Cancel'),
        confirmText: this.translate.instant('Regenerate'),
      },
      presentation: 'dialog',
    });
    return firstValueFrom(ref.afterClosed()).then(confirmed => !!confirmed);
  }

  confirmRegenerateAll(hasApproved: boolean): Promise<boolean> {
    const ref = this.dialog.open<ConfirmComponent, ConfirmDialogData, boolean>(ConfirmComponent, {
      data: {
        title: this.translate.instant('Regenerate all documents'),
        description: this.translate.instant('Regenerating will create a new version of all the documents'),
        warning: hasApproved
          ? `${this.translate.instant('Approved documents will not be regenerated.')} ${this.translate.instant('All the changes / edits you did to your document will be lost')}`
          : this.translate.instant('All the changes / edits you did to your document will be lost'),
        confirmIcon: 'refresh',
        cancelText: this.translate.instant('Cancel'),
        confirmText: this.translate.instant('Regenerate all'),
      },
      presentation: 'dialog',
    });
    return firstValueFrom(ref.afterClosed()).then(confirmed => !!confirmed);
  }

  // ─── Inline per-document error state ──────────────────────────────────────

  flagError(templateId: number, templateType: string): void {
    this.errorKeys.add(this.keyFor(templateId, templateType));
  }

  hasError(doc: CopilotDocument | null | undefined): boolean {
    if (!doc) return false;
    return this.errorKeys.has(this.keyForDoc(doc));
  }

  clearError(doc: CopilotDocument | null | undefined, dictationId: string): void {
    if (!doc) return;
    const key = this.keyForDoc(doc);
    this.errorKeys.delete(key);
    this.batchWatcher.resolveFailure(dictationId, key);
  }

  clearErrors(docs: CopilotDocument[], dictationId: string): void {
    for (const doc of docs) {
      const key = this.keyForDoc(doc);
      this.errorKeys.delete(key);
      this.batchWatcher.resolveFailure(dictationId, key);
    }
  }

  // ─── Regenerate-all completion ────────────────────────────────────────────

  watchRegenerateAll(dictationId: string, trackedDocs: CopilotDocument[]): void {
    this.batchWatcher.watch(dictationId, trackedDocs);
  }

  isRegeneratingAll(dictationId: string | null | undefined): boolean {
    return this.batchWatcher.isRegeneratingAll(dictationId);
  }

  private keyFor(templateId: number, templateType: string): string {
    return `${templateId}_${templateType}`;
  }

  private keyForDoc(doc: CopilotDocument): string {
    return this.keyFor(doc.templateId, doc.templateType);
  }
}
